import type { ScoreExclusionReason, ScoreInput } from '../index.js'
import { describeCounted } from './countedText.js'

const EXCLUSION_TEXT: Record<ScoreExclusionReason, string> = { switched_off: 'switched off', no_weight: 'no weight', no_data: 'no data yet' }

/**
 * A computed breakdown, one row per behavior: what was counted, the value it
 * earned, how much it counts, and the score points it cost. Behaviors that
 * were not included say why instead. Where the breakdown was imported rather
 * than computed, the engine's columns are simply absent and the row shows the
 * value alone.
 */
export function ScoreBreakdownTable({ inputs }: { inputs: ScoreInput[] }) {
  if (inputs.length === 0) {
    return <div className="text-[11px] text-ink-mid">No score components yet &mdash; nothing has been scored for this worker.</div>
  }
  const hasEngineColumns = inputs.some((input) => input.included !== undefined)

  return (
    <table className="w-full text-[11px]">
      <thead>
        <tr className="text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid">
          <th className="py-1 pr-2 font-bold">Behavior</th>
          {hasEngineColumns && <th className="py-1 pr-2 font-bold">Counted</th>}
          <th className="py-1 pr-2 font-bold text-right">Value</th>
          <th className="py-1 pr-2 font-bold text-right">Weight</th>
          {hasEngineColumns && <th className="py-1 font-bold text-right">Cost</th>}
        </tr>
      </thead>
      <tbody>
        {inputs.map((input) => {
          const excluded = input.included === false
          return (
            <tr key={input.key} className={`border-t border-marine/10 ${excluded ? 'text-ink-mid/70' : 'text-ink'}`}>
              <td className="py-1.5 pr-2">
                <div className="font-semibold">{input.label}</div>
                {input.nudge && <div className="text-[10px] text-coral leading-snug">{input.nudge}</div>}
              </td>
              {hasEngineColumns && <td className="py-1.5 pr-2 whitespace-nowrap">{excluded ? `Not counted: ${input.excludedReason ? EXCLUSION_TEXT[input.excludedReason] : 'not included'}` : (describeCounted(input) ?? '—')}</td>}
              <td className="py-1.5 pr-2 text-right font-bold">{excluded ? '—' : input.value.toFixed(1)}</td>
              <td className="py-1.5 pr-2 text-right">{input.weight}%</td>
              {hasEngineColumns && <td className="py-1.5 text-right font-bold text-coral">{excluded || input.pointsLost === null || input.pointsLost === undefined ? '—' : `−${input.pointsLost.toFixed(1)}`}</td>}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
